import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  roleAccessQuery,
  roleTicketScopeQuery,
  saveRoleAccess,
  saveRoleTicketScope,
  type RoleAccessOption,
  type TicketScope,
} from '../../api/settingsUsers'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { RaSection } from './AccountRoles'
import { failed } from './shared'
import './RolesAccess.css'

/** Valoarea din `<select>`: '' = "Not set". */
const NOT_SET = ''

/**
 * O propozitie scurta sub selectul de nivel, pentru optiunea ALEASA. Nivelul
 * spune CE FIRME vede omul; ce poate face vine din grila de dedesubt.
 */
const LEVEL_LINE: Readonly<Record<string, string>> = {
  [NOT_SET]: 'Not set: each account keeps the level set by hand.',
  '1': 'Admin: sees only the companies of the offices the person is assigned to.',
  '2': 'Super Admin: sees the whole own company.',
  '3': 'Service Provider: sees all companies of the provider. Never given from the directory.',
  '6': 'IT Service Catalog: signs in only to the IT Service Catalog, not to the portal.',
}

const SCOPE_LINE: Readonly<Record<TicketScope, string>> = {
  all: 'All: every ticket of the companies the person sees.',
  groups: 'Own groups: tickets of the person’s ticket groups, plus those assigned to or created by them.',
  own: 'Own only: only tickets assigned to or created by the person.',
}

function levelValue(value: number | null): string {
  return value === null ? NOT_SET : String(value)
}

function levelLine(level: string, options: readonly RoleAccessOption[]): string {
  if (LEVEL_LINE[level]) return LEVEL_LINE[level]
  const option = options.find((o) => levelValue(o.value) === level)
  return option ? `${option.label}: ${option.help}` : ''
}

/**
 * "Role settings" (19 octombrie 2026): cardul de deasupra grilei de drepturi,
 * in locul celor doua randuri inline ("Access in the platform" si "Tickets
 * visible", fiecare cu Save-ul lui si cu o propozitie lunga pe acelasi rand).
 *
 * Doua campuri alaturate (unul sub altul pe ecran ingust), fiecare cu
 * explicatia optiunii alese sub el, si UN singur buton care le salveaza pe
 * amandoua - prin aceleasi doua capete de pana acum, numai ce s-a schimbat.
 * Un singur mesaj; daca unul pica, mesajul spune care.
 *
 *  - Access in the platform: nivelul pe care il da rolul (Admin, Super Admin,
 *    Service Provider, IT Service Catalog) sau "Not set" - fiecare cont isi
 *    pastreaza nivelul pus de mana. Il primesc conturile care isi iau accesul
 *    din rol si cele venite din director (afara de Service Provider).
 *  - Tickets visible: All / Own groups / Own only. Cu mai multe roluri,
 *    castiga cea mai larga treapta.
 *
 * Serverul pazeste tot: cine poate schimba, ce nivel poate da, rolurile de
 * sistem.
 */
export function RoleSettings({ roleId }: { roleId: number }) {
  const client = useQueryClient()
  const access = useQuery(roleAccessQuery(roleId))
  const tickets = useQuery(roleTicketScopeQuery(roleId))
  const [level, setLevel] = useState<string>(NOT_SET)
  const [scope, setScope] = useState<TicketScope>('all')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (access.data) setLevel(levelValue(access.data.level))
  }, [access.data])

  useEffect(() => {
    if (tickets.data) setScope(tickets.data.scope)
  }, [tickets.data])

  const loading = (!access.data && !access.error) || (!tickets.data && !tickets.error)
  if (loading) return <Spinner />

  const savedLevel = access.data ? levelValue(access.data.level) : NOT_SET
  const levelChanged = !!access.data && level !== savedLevel
  const scopeChanged = !!tickets.data && scope !== tickets.data.scope
  const mayEdit = !!access.data?.mayEdit || !!tickets.data?.mayEdit

  /* Service Provider se ofera numai administratorului de platforma - si ramane
     in lista cand e chiar nivelul salvat, ca sa se vada. */
  const mayGiveProvider = !!access.data?.mayGiveProvider
  const levelOptions = (access.data?.options ?? []).filter((o) => o.value !== 3 || mayGiveProvider || savedLevel === '3')

  const save = () => {
    const jobs: Promise<string | null>[] = []
    let names: string[] = []
    if (levelChanged) {
      names = [...names, 'Access in the platform']
      jobs.push(
        saveRoleAccess(roleId, level === NOT_SET ? null : Number(level)).then((result) => {
          void client.invalidateQueries({ queryKey: ['settings', 'users', 'roles', roleId, 'access'] })
          return result.updated > 0 ? `${result.updated} account(s) updated` : null
        }),
      )
    }
    if (scopeChanged) {
      names = [...names, 'Tickets visible']
      jobs.push(
        saveRoleTicketScope(roleId, scope).then(() => {
          void client.invalidateQueries({ queryKey: ['settings', 'users', 'roles', roleId, 'ticket-scope'] })
          return null
        }),
      )
    }
    if (jobs.length === 0) return

    setBusy(true)
    void Promise.allSettled(jobs)
      .then((results) => {
        const bad = results
          .map((result, i) => (result.status === 'rejected' ? `${names[i]} was not saved: ${failed(result.reason)}` : null))
          .filter((line): line is string => line !== null)
        const good = results
          .map((result, i) => (result.status === 'fulfilled' ? names[i] : null))
          .filter((name): name is string => name !== null)
        if (bad.length === 0) {
          const notes = results
            .map((result) => (result.status === 'fulfilled' ? result.value : null))
            .filter((note): note is string => !!note)
          toast(notes.length > 0 ? `Role settings saved; ${notes.join('; ')}` : 'Role settings saved', 'Success')
        } else {
          const kept = good.length > 0 ? ` ${good.join(' and ')} was saved.` : ''
          toast(`${bad.join(' ')}${kept}`, 'Error', 'error')
        }
      })
      .finally(() => setBusy(false))
  }

  const members = access.data?.members ?? 0

  return (
    <div className="od-ra od-role-settings" data-testid="role-settings">
      <RaSection title="Role settings">
        <p className="od-ra-lead">
          Apply to the accounts that take their access from this role{members > 0 ? ` (${members} now)` : ''}, and to
          those coming from the directory.
        </p>
        {/* Unul sub altul: cardul sta in coloana ingusta din dreapta grilei de drepturi. */}
        <div className="od-role-settings-fields" data-testid="role-settings-fields">
          <div className="od-ra-field" data-testid="role-access-level">
            <label className="control-label" htmlFor={`role-access-level-${roleId}`}>
              Access in the platform
            </label>
            {access.error ? (
              <div className="alert alert-danger">{failed(access.error)}</div>
            ) : (
              <>
                <select
                  id={`role-access-level-${roleId}`}
                  className="form-control"
                  value={level}
                  disabled={!access.data?.mayEdit || busy}
                  aria-describedby={`role-access-level-${roleId}-help`}
                  onChange={(event) => setLevel(event.target.value)}
                >
                  {levelOptions.map((option) => (
                    <option key={option.value ?? 'none'} value={levelValue(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="help-block" id={`role-access-level-${roleId}-help`}>
                  {levelLine(level, levelOptions)}
                </span>
              </>
            )}
          </div>
          <div className="od-ra-field" data-testid="role-ticket-scope">
            <label className="control-label" htmlFor={`role-ticket-scope-${roleId}`}>
              Tickets visible
            </label>
            {tickets.error ? (
              <div className="alert alert-danger">{failed(tickets.error)}</div>
            ) : (
              <>
                <select
                  id={`role-ticket-scope-${roleId}`}
                  className="form-control"
                  value={scope}
                  disabled={!tickets.data?.mayEdit || busy}
                  aria-describedby={`role-ticket-scope-${roleId}-help`}
                  onChange={(event) => setScope(event.target.value as TicketScope)}
                >
                  {(tickets.data?.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="help-block" id={`role-ticket-scope-${roleId}-help`}>
                  {SCOPE_LINE[scope]} With several roles, the widest applies.
                </span>
              </>
            )}
          </div>
        </div>
        {mayEdit ? (
          <div className="od-ra-actions">
            <button
              type="button"
              className="btn btn-success"
              disabled={(!levelChanged && !scopeChanged) || busy}
              onClick={save}
            >
              {busy ? 'Saving...' : 'Save role settings'}
            </button>
          </div>
        ) : null}
      </RaSection>
    </div>
  )
}
